// TrainerLayout.js
import React, { useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import TrainerSidebar from './TrainerSidebar';
import MobileTrainerSidebar from './MobileTrainerSidebar';
import '../../styles/TrainerLayout.css';
import { TrainerContext } from "../../context/TrainerContext";
import TopNavBar from '../commons/TopNavBar';
import TrainerOnboarding from '../onboarding/TrainerOnboarding';

/**
 * TrainerLayout - Main layout wrapper for trainer pages
 *
 * Architecture:
 * ┌─────────────────────────────────────────────────────────────┐
 * │                    layout-container                         │
 * │  ┌──────────┐  ┌──────────────────────────────────────────┐ │
 * │  │          │  │              main-area                   │ │
 * │  │ sidebar  │  │  ┌────────────────────────────────────┐  │ │
 * │  │ (fixed)  │  │  │         TopNavBar                  │  │ │
 * │  │          │  │  ├────────────────────────────────────┤  │ │
 * │  │          │  │  │         page-content               │  │ │
 * │  │          │  │  │    ┌────────────────────────┐      │  │ │
 * │  │          │  │  │    │      {children}        │      │  │ │
 * │  │          │  │  │    └────────────────────────┘      │  │ │
 * │  └──────────┘  └──────────────────────────────────────────┘ │
 * └─────────────────────────────────────────────────────────────┘
 *
 * Props:
 * @param {React.ReactNode} children - Page content to render
 * @param {boolean} noPadding - If true, removes default padding from page-content
 * @param {boolean} fullWidth - If true, removes max-width constraint (for calendars, tables, etc.)
 * @param {string} className - Additional class name for page-content
 */
const TrainerLayout = ({
                           children,
                           noPadding = false,
                           fullWidth = true,
                           className = ''
                       }) => {
    const { trainer } = useContext(TrainerContext);
    const location = useLocation();

    // Initialize state from localStorage or default to false
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
        const saved = localStorage.getItem('trainerSidebarCollapsed');
        return saved !== null ? JSON.parse(saved) : false;
    });

    // Save state to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('trainerSidebarCollapsed', JSON.stringify(isSidebarCollapsed));
    }, [isSidebarCollapsed]);

    const toggleSidebarCollapse = () => {
        setIsSidebarCollapsed(!isSidebarCollapsed);
    };

    // Build page-content class names
    const pageContentClasses = [
        'page-content',
        noPadding && 'page-content--no-padding',
        fullWidth && 'page-content--full-width',
        className
    ].filter(Boolean).join(' ');

    return (
        <div className={`layout-container ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
            {/* Desktop sidebar (hidden on mobile via CSS) */}
            <aside className="sidebar">
                <TrainerSidebar
                    trainer={trainer}
                    isCollapsed={isSidebarCollapsed}
                    onToggleCollapse={toggleSidebarCollapse}
                    activeRoute={location.pathname}
                />
            </aside>

            {/* Mobile sidebar with overlay */}
            <div className="mobile-sidebar-container">
                <MobileTrainerSidebar />
            </div>

            {/* Main content area - handles all positioning relative to sidebar */}
            <main className="main-area">
                <TopNavBar pageTitle="Dashboard" userId={trainer?.id} />

                {/* Page content wrapper - children style relative to THIS container */}
                <div className={pageContentClasses}>
                    {children}
                </div>
            </main>

            {/* Trainer Onboarding Walkthrough */}
            <TrainerOnboarding />
        </div>
    );
};

export default TrainerLayout;